import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAdminVehicleDepreciation,
  recalculateAllDepreciation,
  recalculateListingDepreciation,
} from "../../../../services/vehicleDepreciationAdminApi";
import { vehicleMarketDetailPath } from "../../../../constants/transport";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCop } from "../../mobility/vehicleMarketFormat";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const VehicleDepreciationAdminPage = ({ onClose }) => {
  const [overview, setOverview] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminVehicleDepreciation({ limit: 200 });
      setOverview(data.overview || null);
      setListings(data.listings || []);
    } catch (err) {
      setError(err.message || "Unable to load depreciation dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalcAll = async () => {
    try {
      const r = await recalculateAllDepreciation({ limit: 500 });
      setToast(`Recalculated ${r.result?.processed ?? 0} listings.`);
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  const handleRecalcRow = async (id) => {
    try {
      await recalculateListingDepreciation(id);
      setToast("Estimate recalculated.");
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  return (
    <div className="admin-dashboard admin-vehicle-depreciation-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1>Vehicle Depreciation</h1>
          <p className="muted">Listing-level depreciation vs new vehicle baselines (M23).</p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="crm-btn crm-btn-secondary" onClick={handleRecalcAll}>
            Recalculate batch
          </button>
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}
      {error ? <AdminStatePanel variant="error" message={error} /> : null}

      {overview ? (
        <div className="admin-summary-grid">
          <div className="admin-summary-card">
            <span>With estimate</span>
            <strong>{overview.listingsWithEstimate}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Insufficient</span>
            <strong>{overview.insufficientData}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Holds value</span>
            <strong>{overview.holdsValueWell}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Too close to new</span>
            <strong>{overview.tooCloseToNew}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Avg dep. %</span>
            <strong>{overview.averageDepreciationPct ?? "—"}</strong>
          </div>
        </div>
      ) : null}

      {loading ? (
        <AdminStatePanel message="Loading…" />
      ) : (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Source</th>
                <th>Used</th>
                <th>New</th>
                <th>Dep. %</th>
                <th>Annualized</th>
                <th>Label</th>
                <th>Conf.</th>
                <th>Year</th>
                <th>Mileage</th>
                <th>Calculated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listings.map((row) => (
                <tr key={row.vehicleListingId}>
                  <td>{row.title}</td>
                  <td>{row.sourceName}</td>
                  <td>{formatCop(row.currentPriceCop)}</td>
                  <td>{formatCop(row.newPriceCop)}</td>
                  <td>{row.depreciationPct ?? "—"}</td>
                  <td>{row.annualizedDepreciationPct ?? "—"}</td>
                  <td>{row.depreciationLabel || "—"}</td>
                  <td>{row.confidenceLabel || "—"}</td>
                  <td>{row.modelYear ?? "—"}</td>
                  <td>{row.mileageKm ?? "—"}</td>
                  <td>{formatDate(row.calculatedAt)}</td>
                  <td>
                    <Link to={vehicleMarketDetailPath(row.vehicleListingId)}>Detail</Link>
                    {" · "}
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() => handleRecalcRow(row.vehicleListingId)}
                    >
                      Recalc
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      )}
    </div>
  );
};

export default VehicleDepreciationAdminPage;
